import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import { HeartIcon as HeartIconOutline } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { addToFavourites, removeFromFavourites, UserState } from "../../slices/auth";
import { addAlert } from "../../slices/alert";
import { generateTranslatedText } from "../../utils/boilerplate";
import Spinner from "./Spinner";
import { FavouriteObjectType } from "../../types/content";

declare global {
  interface Window {
    dataLayer: any[];
  }
}

/** Which `profile` field holds the favourites for each object type. */
const FAVOURITE_FIELD: Record<FavouriteObjectType, keyof UserState["profile"]> = {
  Post: "favourite_posts",
  People: "favourite_people",
  Event: "favourite_events",
  Tool: "favourite_tools",
  Media: "favourite_media",
  Topic: "favourite_topics",
  Collection: "favourite_collections",
  DirectoryEntry: "favourite_directory_entries",
};

export default function FavouriteButton({
  objectId,
  objectType,
  dataTitle,
  dataAuthor,
  dataTags,
  className = "absolute top-2 right-2",
}: {
  objectId: string;
  objectType: FavouriteObjectType;
  dataTitle: string;
  dataAuthor: string;
  dataTags: string;
  /** Positioning classes. Defaults to pinning the button to a card's top-right. */
  className?: string;
}) {
  const dispatch = useAppDispatch();

  const { user, favourites: { id: favouriteId, loading: favouriteLoading } } = useAppSelector((state) => state.auth);
  const isLoggedIn = !!user;

  const favourites = user?.profile[FAVOURITE_FIELD[objectType]] as { id: string }[] | undefined;
  const check = !!favourites?.some(x => x.id === objectId);

  /**
   * On rejection the thunk's payload is the server's reason string, not a
   * `{status}` object — show it when we have one. A generic fallback here is what
   * made a missing group permission indistinguishable from an unverified email.
   */
  function alertFailure(res: any, fallbackKey: string) {
    const reason = typeof res?.payload === "string" ? res.payload : "";
    dispatch(addAlert(reason || generateTranslatedText(fallbackKey, "EN"), "orange"));
  }

  async function handleRemove(e: any) {
    const res: any = await dispatch(removeFromFavourites({ id: objectId, type: objectType }));
    if (res.payload?.status === 200) {
      dispatch(addAlert(generateTranslatedText("removed_from_favourites", "EN"), "mint-green"))
    } else {
      alertFailure(res, "error_removing_from_favourites");
    }
  }

  async function handleAdd(e: any) {
    const res: any = await dispatch(addToFavourites({ id: objectId, type: objectType }));
    if (res.payload?.status === 200) {
      dispatch(addAlert(generateTranslatedText("added_to_favourites", "EN"), "mint-green"))
    } else {
      alertFailure(res, "error_adding_to_favourites");
    }
  }

  return (
    <span
      className={`
        ${className} bg-black/20 rounded-full p-2 group overflow-visible
        ${isLoggedIn ? "cursor-pointer" : "cursor-not-allowed"}
      `}
      onClick={(e) => {
        e.stopPropagation(); // <- prevent click from bubbling to parent
        if (!isLoggedIn) return;  // Do nothing if not logged in
        if (check) {
          handleRemove(e);
        } else {
          handleAdd(e);
        }
        // Trigger GTM event
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "favouriteContent",
          contentTitle: dataTitle,
          contentAuthor: dataAuthor,
          contentTags: dataTags,
        });
      }}
      data-title={dataTitle}
      data-author={dataAuthor}
      data-tags={dataTags}
    >
      {favouriteLoading && favouriteId === objectId ? (
        <Spinner className="w-6 h-6 text-white" />
      ) : check ? (
        <HeartIconSolid className="w-6 h-6 text-white" />
      ) : (
        <HeartIconOutline className="w-6 h-6 text-white" />
      )}

      {/* Tooltip if not logged in */}
      {!isLoggedIn && (
        <span
          className="
            absolute left-1/2 top-full mt-1 -translate-x-1/2
            px-2 py-1 bg-black text-white text-xs rounded
            opacity-0 group-hover:opacity-100 pointer-events-none z-50
          "
        >
          You must be logged in to favourite content
        </span>
      )}
    </span>
  );
}
